import Button from '@carvana/ds-button';
import cx from 'classnames';
import { useContext, useMemo } from 'react';
import ModalContext from '~/context/vdp/ModalContext';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';

type ErrorContentVariant = {
  heading: string;
  description: React.ReactNode;
  analyticsId: string;
  testId: string;
};

function getErrorVariant(status: {
  notAvailableInCA: boolean;
  outOfMarket: boolean;
  outOfMarketNewCar: boolean;
}): ErrorContentVariant | null {
  if (status.outOfMarketNewCar) {
    return {
      heading: 'Brand new vehicles aren’t in your area yet',
      description: 'Change your location or browse used vehicles near you.',
      analyticsId: 'Out of Market New - Geolocation',
      testId: 'out-of-market-new-change-location'
    };
  }

  if (status.outOfMarket) {
    return {
      heading: 'We’re not in your area yet',
      description: (
        <>
          Unfortunately, we don’t currently service this area. Try changing your
          location to find an area we service near you.{' '}
          <a
            aria-label="Learn more about our service area (opens in a new tab)"
            href="/help/carvana-inventory/where-are-carvana-cars-located"
            target="_blank"
            rel="noopener noreferrer"
            className="t-link-sm"
            data-adobe-id="out-of-market-learn-more"
            data-analytics-id="Out of Market - Learn More"
            data-testid="out-of-market-learn-more"
          >
            Learn more
          </a>
        </>
      ),
      analyticsId: 'Out of Market - Geolocation',
      testId: 'out-of-market-change-location'
    };
  }

  if (status.notAvailableInCA) {
    return {
      heading: 'Not available in California',
      description:
        'We can’t verify that this vehicle meets California emissions standards, and unfortunately cannot sell it to California residents.',
      analyticsId: 'Not Available in CA - Geolocation',
      testId: 'not-available-ca-change-location'
    };
  }

  return null;
}

type OwnProps = {
  showDesktopActions?: boolean;
};

export default function OutOfAreaErrorContent({
  showDesktopActions = false
}: OwnProps) {
  const { setGeolocationModalOpen } = useContext(ModalContext);
  const { notAvailableInCA, outOfMarket, outOfMarketNewCar } = useAlertStatus();

  const variant = useMemo(
    () =>
      getErrorVariant({
        notAvailableInCA,
        outOfMarket,
        outOfMarketNewCar
      }),
    [notAvailableInCA, outOfMarket, outOfMarketNewCar]
  );

  return variant ? (
    <div
      role="alert"
      className={cx(
        'flex flex-col items-start gap-xl self-stretch bg-background-weak -mx-2xl -mb-2xl -mt-lg px-2xl pb-2xl pt-xl lg:-mx-4xl lg:-mb-4xl lg:-mt-0 lg:gap-none lg:px-4xl lg:pb-4xl lg:pt-3xl',
        showDesktopActions ? 'sm:rounded-b-none' : 'sm:rounded-b-xl'
      )}
    >
      <h3 className="self-stretch text-text-strong t-heading-sm lg:flex-1 lg:t-heading-md">
        {variant.heading}
      </h3>
      <p className="self-stretch text-text-strong t-body-sm">
        {variant.description}
      </p>
      <Button
        data-adobe-id="out-of-area-change-location"
        data-analytics-id={variant.analyticsId}
        data-testid={variant.testId}
        onClick={() => setGeolocationModalOpen(true)}
        variant="secondary"
        size="md"
        width="auto"
        className="w-full self-stretch lg:h-[56px] lg:py-[14px]"
      >
        Change Location
      </Button>
    </div>
  ) : null;
}
